import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { X, PencilSimple, ClockCounterClockwise } from "@phosphor-icons/react";

const DEPARTMENTS = ["Executive", "Consulting", "Investigations", "Legal", "Finance", "HR", "Quality", "Operations", "Field"];

export default function EditUserModal({ user, onClose, onSaved }) {
  const { user: me } = useAuth();
  const isCEO = (me?.ux_tier || 0) >= 5;

  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    department: user.department || "Investigations",
    designation: user.designation || "",
    role: user.role || "",
    initials: user.initials || "",
    rm_md: user.rm_md || "",
    ux_tier: user.ux_tier || 1,
    reports_to: user.reports_to || "",
    phone: user.phone || "",
  });
  const [managers, setManagers] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("edit"); // edit | history
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    // Load potential managers (Tier 3+)
    api.get("/team").then(r => {
      const all = (r.data || []).filter(u => u.user_id !== user.user_id && (u.ux_tier || 0) >= 3);
      setManagers(all);
    }).catch(() => setManagers([]));

    // Load employment history
    api.get(`/users/${user.user_id}/employment-history`).then(r => {
      setHistory(r.data?.history || []);
    }).catch(() => setHistory([]));
  }, [user.user_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      // Build patch — only include changed fields
      const patch = {};
      Object.keys(form).forEach(k => {
        if (form[k] !== (user[k] || "")) patch[k] = form[k] === "" ? null : form[k];
      });
      if (!Object.keys(patch).length) {
        setErr("No changes to save.");
        setBusy(false);
        return;
      }
      const { data } = await api.put(`/admin/users/${user.user_id}/edit`, patch);
      // If reports_to changed, surface the cascade summary so the user knows downstream
      // artifacts (pending leaves, open cases) were reconciled.
      const prop = data?.propagation;
      if (prop && (prop.leave_requests_rerouted || prop.cases_restamped || prop.descendants_affected)) {
        const lines = [
          `${prop.leave_requests_rerouted} pending leave request(s) rerouted to the new manager`,
          `${prop.cases_restamped} open case(s) re-stamped with refreshed RM/MD`,
          `${prop.descendants_affected} downstream report(s) affected`,
        ];
        alert("Reporting change applied. Propagated changes:\n\n• " + lines.join("\n• "));
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Save failed");
    } finally { setBusy(false); }
  };

  const fmtVal = (v) => v === null || v === undefined || v === "" ? "(empty)" : String(v);
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" data-testid="edit-user-modal">
      <div className="bg-[var(--surface,#fff)] rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PencilSimple size={20} weight="duotone" className="text-[var(--brand-primary)]" />
            <div>
              <h2 className="font-bold text-[var(--text-primary)]">Edit team member</h2>
              <div className="text-[11px] text-[var(--text-muted)]">{user.name} · {user.initials}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="edit-modal-close"><X size={18} /></button>
        </div>

        <div className="border-b border-[var(--border)] flex gap-2 px-5">
          <button data-testid="tab-edit" onClick={() => setTab("edit")}
            className={`text-[12px] py-2.5 px-1 border-b-2 ${tab === "edit" ? "border-[var(--brand-primary)] text-[var(--brand-primary)] font-semibold" : "border-transparent text-[var(--text-muted)]"}`}>
            Edit details
          </button>
          <button data-testid="tab-history" onClick={() => setTab("history")}
            className={`text-[12px] py-2.5 px-1 border-b-2 flex items-center gap-1 ${tab === "history" ? "border-[var(--brand-primary)] text-[var(--brand-primary)] font-semibold" : "border-transparent text-[var(--text-muted)]"}`}>
            <ClockCounterClockwise size={12} /> History ({history.length})
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {tab === "edit" && (
            <div className="space-y-3" data-testid="edit-form">
              <Row><Field label="Full name" value={form.name} onChange={v => set("name", v)} testid="edit-name" /></Row>
              <Row>
                <Field label="Email" value={form.email} onChange={v => set("email", v)} type="email" testid="edit-email" />
                <Field label="Phone" value={form.phone} onChange={v => set("phone", v)} testid="edit-phone" />
              </Row>
              <Row>
                <Select label="Department" value={form.department} options={DEPARTMENTS} onChange={v => set("department", v)} testid="edit-department" />
                <Field label="Initials (unique)" value={form.initials} onChange={v => set("initials", v.toUpperCase())} testid="edit-initials" />
              </Row>
              <Row>
                <Field label="Designation (title)" value={form.designation} onChange={v => set("designation", v)} testid="edit-designation" />
                <Field label="Role" value={form.role} onChange={v => set("role", v)} testid="edit-role" />
              </Row>
              <Row>
                <Select label="RM / MD flag" value={form.rm_md} options={["", "RM", "MD"]} optionLabels={["(none)", "Relationship Manager", "Mentor Director"]} onChange={v => set("rm_md", v)} testid="edit-rm-md" />
                <Select label={`UX Tier ${!isCEO ? "(CEO-only)" : ""}`} value={form.ux_tier} options={[1, 2, 3, 4, 5]} disabled={!isCEO} onChange={v => set("ux_tier", parseInt(v))} testid="edit-ux-tier" />
              </Row>
              <Row>
                <Select
                  label="Reports to (manager)"
                  value={form.reports_to}
                  options={["", ...managers.map(m => m.user_id)]}
                  optionLabels={["(no manager)", ...managers.map(m => `${m.name} (${m.initials || "?"}) · ${m.designation || m.role || "Tier " + m.ux_tier}`)]}
                  onChange={v => set("reports_to", v)}
                  testid="edit-reports-to"
                />
              </Row>
              {err && <div className="text-[12px] text-[var(--status-danger)] bg-[var(--status-danger)]/10 px-3 py-2 rounded" data-testid="edit-error">{err}</div>}
            </div>
          )}

          {tab === "history" && (
            <div data-testid="history-pane">
              {history.length === 0 ? (
                <div className="text-[12px] text-[var(--text-muted)] text-center py-8">
                  No history yet. Future edits to name, role, department, designation, initials, RM/MD or manager will appear here with effective-from / effective-to dates.
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map(h => (
                    <div key={h.history_id} className="border border-[var(--border)] rounded p-3 text-[12px]" data-testid={`history-${h.history_id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-[var(--brand-primary)] uppercase text-[10px] tracking-wider">{h.field}</span>
                        <span className="text-[10px] text-[var(--text-muted)]">
                          {fmtDate(h.effective_from)}{h.effective_to ? ` → ${fmtDate(h.effective_to)}` : " → present"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-[var(--surface-2)] p-2 rounded">
                          <div className="text-[9px] uppercase text-[var(--text-muted)] mb-0.5">Was</div>
                          <div className="font-mono text-[11px]">{fmtVal(h.old_value)}</div>
                        </div>
                        <div className="bg-[var(--brand-primary)]/5 p-2 rounded">
                          <div className="text-[9px] uppercase text-[var(--text-muted)] mb-0.5">Became</div>
                          <div className="font-mono text-[11px]">{fmtVal(h.new_value)}</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-[var(--text-muted)] mt-1.5">
                        Changed by {h.changed_by_user_name || h.changed_by_user_id || "system"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {tab === "edit" && (
          <div className="border-t border-[var(--border)] px-5 py-3 flex justify-end gap-2">
            <button onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
            <button data-testid="edit-save" disabled={busy} onClick={save} className="span-btn">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>; }

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="span-input w-full text-[13px]" />
    </div>
  );
}

function Select({ label, value, options, optionLabels, disabled, onChange, testid }) {
  const labels = optionLabels || options.map(String);
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">{label}</label>
      <select data-testid={testid} value={value || ""} disabled={disabled} onChange={e => onChange(e.target.value)} className="span-input w-full text-[13px] disabled:opacity-50">
        {options.map((o, i) => <option key={String(o)} value={o}>{labels[i] || String(o)}</option>)}
      </select>
    </div>
  );
}
